import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MotionReveal } from "./landing/MotionReveal";

const AUTOPLAY_MS = 4200;

function toPublicPath(path) {
  if (!path) return "";
  return path.startsWith("/") ? path : `/${path}`;
}

export const Gallery = () => {
  const { t } = useTranslation("global");
  const list = t("gallery.items", { returnObjects: true });
  const items = useMemo(() => (Array.isArray(list) ? list : []), [list]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const current = items[active] || items[0];
  const total = items.length;

  const goTo = useCallback(
    (index) => {
      if (!total) return;
      setActive(((index % total) + total) % total);
    },
    [total]
  );

  const goPrev = useCallback(() => goTo(active - 1), [active, goTo]);
  const goNext = useCallback(() => goTo(active + 1), [active, goTo]);

  useEffect(() => {
    if (paused || total < 2) return undefined;

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;

    const id = window.setInterval(() => {
      setActive((prev) => (prev + 1) % total);
    }, AUTOPLAY_MS);

    return () => window.clearInterval(id);
  }, [paused, total]);

  const onKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goPrev();
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      goNext();
    }
  };

  const onTouchStart = (event) => {
    touchStartX.current = event.changedTouches[0]?.clientX ?? null;
    setPaused(true);
  };

  const onTouchEnd = (event) => {
    if (touchStartX.current == null) {
      setPaused(false);
      return;
    }
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) >= 40) {
      if (delta > 0) goPrev();
      else goNext();
    }
    setPaused(false);
  };

  if (!current) return null;

  return (
    <div id="gallery" className="siat-media-col">
      <MotionReveal className="siat-intro">
        <p className="siat-eyebrow">{t("navigation.gallery")}</p>
        <h2>{t("gallery.title")}</h2>
        <p className="siat-lead">{t("gallery.paragraph")}</p>
      </MotionReveal>

      <div
        className="siat-carousel"
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label={t("gallery.title")}
        onKeyDown={onKeyDown}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <div className="siat-carousel__frame">
          <button
            type="button"
            className="siat-carousel__arrow"
            onClick={goPrev}
            aria-label={t("gallery.prev")}
          >
            ‹
          </button>

          <div className="siat-ink-stage siat-ink-stage--gallery">
            <img
              key={current.title}
              className="siat-shot siat-shot--gallery"
              src={toPublicPath(current.smallImage || current.largeImage)}
              alt={current.title}
            />
          </div>

          <button
            type="button"
            className="siat-carousel__arrow"
            onClick={goNext}
            aria-label={t("gallery.next")}
          >
            ›
          </button>
        </div>

        <div className="siat-carousel__meta">
          <p className="siat-eyebrow">
            {String(active + 1).padStart(2, "0")} /{" "}
            {String(total).padStart(2, "0")}
          </p>
          <h3>{current.title}</h3>
          <p>{current.text}</p>
        </div>

        <div className="siat-carousel__dots" role="tablist">
          {items.map((item, index) => (
            <button
              key={item.title}
              type="button"
              role="tab"
              aria-selected={index === active}
              aria-label={item.title}
              className={
                index === active
                  ? "siat-carousel__dot is-active"
                  : "siat-carousel__dot"
              }
              onClick={() => goTo(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
